import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import cwLogo from '../../assets/cw-red-logo.svg';
import { API_ENDPOINTS } from '../../config/api';

interface AppointmentTaker {
  email: string;
  userId: string;
  displayName: string;
  slug: string | null;
  allowSaturday: boolean;
  allowSunday: boolean;
  businessHours: {
    start: number;
    startMinute: number;
    end: number;
    endMinute: number;
  } | null;
  timeZone: string;
}

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [appointmentTakers, setAppointmentTakers] = useState<AppointmentTaker[]>([]);

  useEffect(() => {
    const fetchAppointmentTakers = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.APPOINTMENT_TAKERS);
        if (!response.ok) {
          throw new Error('Failed to fetch appointment takers');
        }
        const data = await response.json();
        if (data.success && data.appointmentTakers) {
          // Filter to only show takers with valid slugs
          const takersWithSlugs = data.appointmentTakers.filter(
            (taker: AppointmentTaker) => taker.slug && taker.slug.trim() !== ''
          );
          
          // Always redirect to eina by default (or first available if eina not found)
          const defaultTaker = takersWithSlugs.find((t: AppointmentTaker) => t.slug === 'eina') || takersWithSlugs[0];
          if (defaultTaker) {
            // Preserve any query params (like ref, session_id for payment returns)
            const queryString = location.search;
            navigate(`/${defaultTaker.slug}${queryString}`, { replace: true });
            return;
          }
          
          setAppointmentTakers(takersWithSlugs);
        }
      } catch (err) {
        console.error('Error fetching appointment takers:', err);
        setError('Unable to load booking options. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAppointmentTakers();
  }, [navigate, location.search]);

  const handleCardClick = (slug: string) => {
    navigate(`/${slug}`);
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f3f4f6',
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: '#f3f4f6',
        px: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          pt: 4,
          pb: 2,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 720 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <img
              src={cwLogo}
              alt="Canada Waleyar"
              style={{
                height: '80px',
                marginBottom: '16px',
                padding: 0,
                borderRadius: 0,
              }}
            />
            <Typography variant="h4" component="h1" gutterBottom>
              Book Your Appointment
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Choose your consultation type to get started
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {appointmentTakers.length === 0 && !error && (
            <Paper
              elevation={0}
              sx={{
                p: 4,
                borderRadius: 2,
                border: '1px solid rgba(15, 15, 15, 0.08)',
                background: '#ffffff',
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No booking options are currently available. Please check back later.
              </Typography>
            </Paper>
          )}

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 3,
              justifyContent: 'center',
            }}
          >
            {appointmentTakers.map((taker) => (
              <Paper
                key={taker.userId}
                elevation={0}
                onClick={() => handleCardClick(taker.slug!)}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid rgba(15, 15, 15, 0.08)',
                  background: '#ffffff',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  width: { xs: '100%', sm: 'calc(50% - 12px)' },
                  minWidth: 280,
                  maxWidth: 340,
                  '&:hover': {
                    borderColor: '#3c3c3c',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #3c3c3c 0%, #5a5a5a 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 16px',
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        color: '#ffffff',
                        fontWeight: 600,
                      }}
                    >
                      {taker.displayName.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </Typography>
                  </Box>
                  
                  <Typography variant="h6" sx={{ mb: 1, fontWeight: 500 }}>
                    {taker.displayName}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Immigration Consultation
                  </Typography>
                  
                  <Box
                    sx={{
                      display: 'inline-block',
                      px: 2,
                      py: 0.75,
                      borderRadius: '16px',
                      background: '#f0f0f0',
                      color: '#3c3c3c',
                      fontWeight: 500,
                      fontSize: '0.875rem',
                    }}
                  >
                    CAD $14.69
                  </Box>
                  
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 2,
                      color: '#5a5a5a',
                      fontWeight: 500,
                    }}
                  >
                    Book Now →
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          py: 2,
          textAlign: 'center',
          width: '100%',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontSize: '0.75rem',
            fontWeight: 300,
          }}
        >
          © 2025 Canadawale Yar™. All rights reserved.
          <br />
          Powered by{' '}
          <Link
            href="https://stripe.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            Stripe
          </Link>
          {' | '}
          <Link
            href="https://canadawaleyar.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            Privacy Policy
          </Link>
          {' | '}
          <Link
            href="https://canadawaleyar.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              }
            }}
          >
            Terms of Service
          </Link>
        </Typography>
      </Box>
    </Box>
  );
};

export default LandingPage;
